import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import {
  bondFlowOf,
  getBondSubmission,
  getResolvedBondConfig,
  BOND_FINAL_STEP_KEY,
  type BondConfig,
  type BondSubmission,
} from "@/lib/brokerBonds";
import { asAddress, type BondRenderField } from "@/lib/bondFields";
import {
  bondOrderProgress,
  bondProfileSections,
  projectAddressField,
  type BondMilestone,
} from "@/lib/bondSubmissionProfile";
import { CapturedValues } from "@/components/BondRecord";
import { SuretyProgramBlock } from "@/components/SuretyProgramBlock";
import { BidResultPanel } from "@/components/BidResultPanel";
import { BondDocumentsSection } from "@/components/BondDocumentsSection";
import { GIA_FOCUS, GiaSection } from "@/components/GiaSection";
import { showsGiaSection } from "@/lib/brokerGia";
import { useBondDocuments } from "@/lib/useBondDocuments";
import { googleMapsSearchUrl } from "@/lib/mapLink";
import {
  bondStatusBadgeClass,
  bondStatusLabel,
  statusBadgeClass,
} from "@/lib/listFormat";
import { formatBondDate } from "@/lib/bondValueDisplay";
import { messageFor } from "@/lib/errors";

// BRK-S140 — one bond order, as a RECORD.
//
// The list has always linked into the WIZARD, which is right for a draft and
// wrong for everything else: a filed order is not a form, and a broker opening
// SB-40711 to check what they submitted had to walk a stepper to read it. So
// `/surety/submissions/{id}` is now the profile, and the flow moved one segment
// down to `/surety/submissions/{id}/continue`.
//
// READ-FIRST, NOT READ-ONLY. A draft is a real state here — the pointer exists
// from draft start, so half the orders in the list are drafts — and it gets a
// **Resume** into the flow rather than a locked page. The one thing a SUBMITTED
// order can change from here is the bid result (BRK-S141), which is why the
// page owns the order in state rather than rendering a snapshot.
//
// ── THE BID-RESULT REMINDER'S DEEP LINK (BRK-S177) ───────────────────────────
//
// PMS's `bid_result_needed` chaser (PMS-S464) links here with
// `?focus=bid-result`, and its "We won" / "We lost" links add `&result=`. This
// page only reads the two parameters and hands them to `BidResultPanel`; the
// scrolling, the focusing and the refusal to preselect over a recorded result
// are the panel's, because they are about the panel's own state.
//
// BOTH VALUES ARE VALIDATED against what this build offers rather than trusted:
// a deep link is a string somebody else composed, and `?result=maybe` must read
// as no suggestion at all rather than as a value the select is asked to take.
//
// The GIA reminder (PMS-S486) follows the same precedent with `?focus=gia`
// (BRK-S182): the page reads the parameter, and `GiaSection` does the scrolling
// and the focusing once its signers have loaded.
//
// ── NO HARDCODED FIELD LIST ──────────────────────────────────────────────────
//
// The sections are built by grouping the CONFIGURATION SNAPSHOT's own final-step
// fields (`bondProfileSections`), and each value is formatted from that field's
// declared type. So a field Greg adds to the configuration appears here with no
// portal release, and a field this build has never heard of lands in a named
// catch-all section rather than disappearing.

/**
 * The `?focus=` value that means "the bid-result panel" (BRK-S177).
 *
 * The one spelling of the deep link's contract on this side, matching PMS's
 * `bidResultUrl` (lib/notification-templates.ts). A constant rather than an
 * inline string, because it is half of an agreement with another repo.
 */
const BID_RESULT_FOCUS = "bid-result";

/** The `?result=` suggestions this build will preselect. A closed list, so a
 *  hand-edited or stale link can only ever mean one of the two or nothing. */
const BID_OUTCOMES = ["won", "lost"] as const;

/** The value bag the final step's terms live in. `final` is a RESERVED key
 *  carrying the WHOLE shared bag, so the final step's own fields are read out of
 *  it BY KEY (see `BondSubmission.values`). */
function finalValues(order: BondSubmission): Record<string, unknown> {
  return order.values?.[BOND_FINAL_STEP_KEY] ?? {};
}

export function SuretySubmissionPage() {
  const { id = "" } = useParams();
  const { authedRequest } = useAuth();
  // The reminder's deep link (BRK-S177), read off the query string and
  // validated against what this build offers — `?result=maybe` is no
  // suggestion rather than a value handed to the control.
  const [params] = useSearchParams();
  const focusBidResult = params.get("focus") === BID_RESULT_FOCUS;
  // The GIA reminder's deep link (BRK-S182, PMS-S486), the same precedent.
  const focusGia = params.get("focus") === GIA_FOCUS;
  const suggestedOutcome = BID_OUTCOMES.find((o) => o === params.get("result"));

  const [order, setOrder] = useState<BondSubmission | null>(null);
  const [config, setConfig] = useState<BondConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The order's documents, read ONCE here and handed to both surfaces that
  // care about them (BRK-S146) — see `useBondDocuments` for why the page owns
  // this rather than the section.
  const {
    documents,
    error: documentsError,
    reload: reloadDocuments,
  } = useBondDocuments(authedRequest, id === "" ? undefined : id);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const fresh = await getBondSubmission(authedRequest, id);
        if (cancelled) return;
        setOrder(fresh);
        // The configuration is resolved from the order's SUBPRODUCT, the same
        // way resume does it (BRK-S088): the order view carries the values and
        // not the labels, and the labels are what make the bag readable.
        //
        // A failed config read is NOT fatal — the header, the timeline and the
        // program block are all readable without it, so it degrades to a record
        // with no term sections rather than to an error page (NN #7).
        try {
          const resolved = await getResolvedBondConfig(
            authedRequest,
            fresh.subproductId,
          );
          if (!cancelled) setConfig(resolved);
        } catch {
          if (!cancelled) setConfig(null);
        }
      } catch (err) {
        if (!cancelled) setError(messageFor(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authedRequest, id]);

  if (error) {
    return (
      <section className="flex w-full flex-col gap-6 py-8">
        <h1 className="text-2xl font-bold tracking-tight">Bond submission</h1>
        <p role="alert" className="text-sm text-brk-danger">
          {error}
        </p>
        <BackLink />
      </section>
    );
  }

  if (!order) {
    return (
      <section className="flex w-full flex-col gap-6 py-8">
        <h1 className="text-2xl font-bold tracking-tight">Bond submission</h1>
        <p role="status" className="text-sm text-brk-dim">
          Loading bond submission…
        </p>
      </section>
    );
  }

  const fields: BondRenderField[] = (config?.finalStep.fields ?? []).map(
    (f) => ({
      name: f.key,
      label: f.label,
      type: f.type,
      required: f.required,
      options: f.options,
      help: f.helpText,
    }),
  );
  const values = finalValues(order);
  // The bag is passed so a section nobody answered is DROPPED rather than
  // rendered as a heading over "nothing captured" — see `bondProfileSections`.
  const sections = bondProfileSections(fields, values);
  const flow = bondFlowOf(order);
  const projectAddress = projectAddressField(fields);
  const mapUrl = projectAddress
    ? googleMapsSearchUrl(asAddress(values[projectAddress.name] as never))
    : null;

  return (
    <section className="flex w-full flex-col gap-6 py-8">
      <header className="flex flex-col gap-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="flex flex-col gap-1">
            <h1 className="font-mono text-2xl font-bold tracking-tight">
              {order.number}
            </h1>
            {/* The insured, and nothing beside it: the bond TYPE is on the
                list row (from the pointer's display snapshot) and the detail
                read does not carry it, so the profile says nothing rather than
                guessing from a subproduct id. */}
            <p className="text-brk-dim">
              {order.accountLegalName ?? "Insured not named"}
            </p>
          </div>
          <span className="flex flex-wrap items-center gap-1.5">
            {/* "Complete" for a filed standalone pre-approval, the same words
                its list row uses (BRK-S160). */}
            <span
              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${bondStatusBadgeClass(
                order.status,
                flow,
              )}`}
            >
              {bondStatusLabel(order.status, flow)}
            </span>
            {order.referred && (
              <span
                className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${statusBadgeClass(
                  "referred",
                )}`}
              >
                Referred
              </span>
            )}
          </span>
        </div>
        <p className="text-sm text-brk-dim">
          Started {formatBondDate(order.createdAt)}
        </p>
      </header>

      <ProgressTimeline milestones={bondOrderProgress(order)} />

      {/* A CANCELLED order (BRK-S181) is kept, not deleted, and reads as what
          it is. No Resume: PMS refuses every write on it (ORDER_CANCELLED), so
          a way back into the flow would be a form that cannot be saved. */}
      {order.status === "cancelled" && (
        <p
          role="status"
          className="rounded-xl border border-brk-border bg-brk-panel p-4 text-sm text-brk-text sm:p-6"
        >
          This request was cancelled. It’s kept here for your records, and
          nothing more will happen on it.
        </p>
      )}

      {/* A DRAFT is a real state here — the pointer exists from draft start, so
          half the list is drafts — and the way on is the flow, not this page. */}
      {order.status === "draft" && (
        <div className="flex flex-col items-start gap-3 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6">
          <p className="text-sm text-brk-text">
            This request isn’t filed yet — pick up where you left off.
          </p>
          <Link
            to={`/surety/submissions/${order.id}/continue`}
            className="rounded-md bg-brk-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brk-accent-strong"
          >
            Resume this request
          </Link>
        </div>
      )}

      {/* THE GIA (BRK-S182) — the SAME component the Request submitted screen
          renders, because the confirmation is transient and this is where the
          reminder lands. A submitted, APPROVED order only: never a draft, never
          a referred order, and not when a GIA is already on file for an
          unchanged roster. It sits above the bid result because it is the one
          thing standing between this order and a bond. */}
      {order.gia && showsGiaSection(order) && (
        <GiaSection orderId={order.id} summary={order.gia} focus={focusGia} />
      )}

      {/* The ONE thing a filed order can still change from here (BRK-S141). On a
          DRAFT there is no filed bid to report against, and the write answers
          409 for one — so the panel is not offered rather than offered and
          refused. */}
      {/* …and NOT on a standalone pre-approval, which has no bid: there is no
          bond request here to win or lose, so the panel is not offered rather
          than offered against nothing (BRK-S160). */}
      {order.status === "submitted" && flow === "bond" && (
        <BidResultPanel
          order={order}
          onUpdated={setOrder}
          focus={focusBidResult}
          initialOutcome={suggestedOutcome}
          // ONE source for the signed contract (BRK-S146). The panel used to
          // read `order.finalContract` while the Documents section read the
          // list — two sources for one fact, which could disagree about
          // whether a contract is on file and rendered its filename twice on
          // one page.
          documents={documents}
          onDocumentsChanged={reloadDocuments}
        />
      )}

      {/* The insured's standing program, rendered by the SAME block the account
          profile uses (BRK-S139). The order's block carries a `pending` status
          the account's cannot, so it is folded to the account vocabulary here
          rather than the component learning two. */}
      {order.preapproval && (
        <SuretyProgramBlock
          accountId={order.accountId}
          preapproval={{
            status:
              order.preapproval.status === "pending"
                ? "none"
                : order.preapproval.status,
            // Carried straight across: the two blocks publish the same closed
            // vocabulary, and a fold would be this page deciding which program
            // an insured holds (BRK-S161).
            programType: order.preapproval.programType,
            // Carried across like the type: the two blocks publish the same
            // derived date, and a fold would be this page deciding when an
            // insured's program lapses.
            expiresAt: order.preapproval.expiresAt,
            terms: order.preapproval.terms,
            evaluatedAt: order.preapproval.evaluatedAt,
            indicationAvailable: order.preapproval.indicationAvailable,
          }}
        />
      )}

      {config === null ? (
        <p className="text-sm text-brk-dim">
          We couldn’t load this bond’s field labels, so the submitted terms
          aren’t shown here. The request itself is unaffected.
        </p>
      ) : flow === "preapproval" && sections.length === 0 ? (
        /* A standalone pre-approval captured no terms — its configuration
           declares an empty final step — so there is nothing to section. One
           line saying WHERE the answer lives beats a page that simply stops
           after the timeline and leaves the broker wondering what they filed
           (BRK-S160). */
        <p className="text-sm text-brk-dim">
          This request was a program pre-approval; the decision lives on the
          account’s Surety program.{" "}
          <Link
            to={`/surety/accounts/${order.accountId}`}
            className="text-brk-accent hover:underline"
          >
            View the account
          </Link>
        </p>
      ) : (
        sections.map((section) => (
          <section
            key={section.id}
            aria-labelledby={`section-${section.id}`}
            className="flex flex-col gap-4 rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6"
          >
            <h2
              id={`section-${section.id}`}
              className="text-lg font-semibold text-brk-text"
            >
              {section.title}
            </h2>
            <CapturedValues
              fields={section.fields}
              values={values}
              emptyNote="Nothing captured in this section."
              extra={(field) =>
                // The project address gains a map link, and only that field:
                // a broker has no reason to drive to the county offices.
                field.name === projectAddress?.name && mapUrl !== null ? (
                  <>
                    {" "}
                    <a
                      href={mapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="whitespace-nowrap text-sm font-normal text-brk-accent hover:underline"
                    >
                      View on map
                    </a>
                  </>
                ) : null
              }
            />
          </section>
        ))
      )}

      {/* The documents attached during intake (BRK-S146). BELOW the terms,
          because it answers "what did I send with it?" and reads as a footnote
          to the request rather than as part of it. Its OWN read, not a slice of
          the order: the value bag carries only the four-field references, and a
          document's type and upload date are the server's (BRK-S145).

          Rendered for a DRAFT too. A broker halfway through an order still
          needs to see what they have already attached, and the section is
          read-only on both — the wizard is where documents are added. */}
      <BondDocumentsSection
        accountId={order.accountId}
        fields={fields}
        documents={documents}
        error={documentsError}
      />

      <BackLink />
    </section>
  );
}

function BackLink() {
  return (
    <Link
      to="/surety/submissions"
      className="text-sm text-brk-accent hover:underline"
    >
      ← Back to bond submissions
    </Link>
  );
}

/** Greg's "Track / Update Progress", built from what the order view states —
 *  see `bondOrderProgress` for why there is no progress field on the wire. */
function ProgressTimeline({
  milestones,
}: {
  milestones: readonly BondMilestone[];
}) {
  return (
    <div className="rounded-xl border border-brk-border bg-brk-panel p-4 sm:p-6">
      {/* An ordered list, and horizontally scrollable rather than wrapped: four
          milestones stacked at 375px push the record off the first screen. */}
      <div className="overflow-x-auto">
        <ol
          aria-label="Request progress"
          className="flex min-w-max items-center gap-3"
        >
          {milestones.map((m, i) => (
            <li key={m.id} className="flex items-center gap-3">
              <span className="flex items-center gap-2">
                <span
                  aria-hidden="true"
                  className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                    m.state === "done"
                      ? "bg-brk-accent/15 text-brk-accent"
                      : m.state === "current"
                        ? "bg-brk-accent text-white"
                        : "bg-brk-bg text-brk-dim ring-1 ring-brk-border"
                  }`}
                >
                  {m.state === "done" ? "✓" : i + 1}
                </span>
                <span
                  className={`whitespace-nowrap text-sm ${
                    m.state === "todo"
                      ? "text-brk-dim"
                      : "font-medium text-brk-text"
                  }`}
                >
                  {m.label}
                </span>
                {/* The state in WORDS, for a screen reader and for anyone who
                    cannot tell the three chip colours apart. */}
                <span className="sr-only">
                  {m.state === "done"
                    ? "complete"
                    : m.state === "current"
                      ? "in progress"
                      : "not started"}
                </span>
              </span>
              {i < milestones.length - 1 && (
                <span
                  aria-hidden="true"
                  className="h-px w-8 shrink-0 bg-brk-border"
                />
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
